import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import ResizeObserver from 'resize-observer-polyfill';

import type { API } from '@/api';
import { CANCEL_CUTOFF_MINUTES } from '@/constants/reservation';
import { SPACE_TYPE } from '@/constants/space';
import { useCurrentTime } from '@/hooks/useCurrentTime';
import { useDateTimeFormat } from '@/hooks/useDateTimeFormat';

import 'simplebar';
import 'simplebar/dist/simplebar.css';

window.ResizeObserver = ResizeObserver;

type StaffReservation =
  API['GET']['StaffReservationSearch']['response']['reservations'][number];

interface StaffOnlyReservationListProps {
  reservations: StaffReservation[];
  selectedIds: Set<string>;
  onToggleSelect: (reservationNo: string) => void;
  spaceType: string;
}

export const StaffOnlyReservationList = ({
  reservations,
  selectedIds,
  onToggleSelect,
  spaceType,
}: StaffOnlyReservationListProps) => {
  const isPrivateRoom = spaceType === SPACE_TYPE.PRIVATE_ROOM;
  const { t } = useTranslation();

  const { formatReservationTimeRangeStaffOnly } = useDateTimeFormat();

  // 定期的に現在時刻を更新（キャンセル可否判定用）
  const now = useCurrentTime();

  // reservationsが変更されたときにsimplebarを再初期化するためのキー
  const simplebarKey = reservations.map((r) => r.reservation_no).join(',');

  return (
    <div className="e-reservation__rows-wrap">
      <div
        key={simplebarKey}
        className="e-reservation__rows"
        data-simplebar
        data-simplebar-auto-hide="false"
      >
        {reservations.map((reservation) => {
          // 予約開始N分前からキャンセル不可
          const cutoffTime = new Date(
            new Date(reservation.starts_at).getTime() -
              CANCEL_CUTOFF_MINUTES * 60 * 1000,
          );
          const isCancelDisabled = cutoffTime <= now;

          // キャンセル済みの予約かどうか
          const isCancelled = reservation.status === 'cancelled';

          return (
            <div
              className={`e-reservation__row ${isCancelled ? 'e-reservation__row--cancelled' : ''}`}
              key={reservation.reservation_no}
            >
              <div className="e-reservation__body">
                {/* 名前・メール */}
                <div
                  className="e-reservation__cell e-reservation__cell--name"
                  data-label={t('staffOnly.header.nameEmail')}
                >
                  {reservation.is_kiosk && (
                    <div className="e-reservation__kiosk-badge">
                      {t('staffOnly.reservation.kioskLabel')}
                    </div>
                  )}
                  <div className="e-reservation__name">
                    {reservation.last_name} {reservation.first_name}
                  </div>
                  <div className="e-reservation__email">
                    {reservation.email}
                  </div>
                </div>

                {/* 予約番号 */}
                <div
                  className="e-reservation__cell e-reservation__cell--no"
                  data-label={t('staffOnly.header.reservationNo')}
                >
                  {reservation.reservation_no}
                </div>

                {/* 日時 */}
                <div
                  className="e-reservation__cell e-reservation__cell--datetime"
                  data-label={t('staffOnly.header.dateTime')}
                >
                  <div>
                    {formatReservationTimeRangeStaffOnly(
                      reservation.starts_at,
                      reservation.ends_at,
                    ).join(' ')}
                  </div>
                </div>

                {/* 施設 */}
                <div
                  className="e-reservation__cell e-reservation__cell--facility"
                  data-label={t('staffOnly.header.facility')}
                >
                  <div>{t(`common.airport.${reservation.airport}`)}</div>
                  <div>{t(`common.terminal.${reservation.terminal}`)}</div>
                  <div>{t(`common.flightType.${reservation.flight_type}`)}</div>
                  <div>{t(`common.lounge.${reservation.lounge}`)}</div>
                  <div>
                    {t('staffOnly.reservation.spaceNo')}{' '}
                    {reservation.space_no ?? '-'}
                  </div>
                </div>

                {/* パスコード / QRコード */}
                <div
                  className="e-reservation__cell e-reservation__cell--pass"
                  data-label={t('staffOnly.header.passCode')}
                >
                  {isPrivateRoom && reservation.pass_code ? (
                    <QRCodeSVG value={reservation.pass_code} size={80} />
                  ) : (
                    reservation.pass_code
                  )}
                </div>
              </div>

              {/* 削除ボタン */}
              <label className="e-reservation__delete">
                <input
                  className="e-reservation__delete-checkbox"
                  type="checkbox"
                  checked={selectedIds.has(reservation.reservation_no)}
                  onChange={() => onToggleSelect(reservation.reservation_no)}
                  disabled={isCancelDisabled || isCancelled}
                />
                <span className="e-reservation__delete-label">
                  {isCancelled
                    ? t('staffOnly.reservation.cancelledLabel')
                    : t('staffOnly.reservation.cancelButton')}
                </span>
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
};
